import React, { useEffect, useState, useContext } from "react";
import { SocketContext } from "../App.js";
import './css/FrontDesk.css';
import ThemeToggle from "./ThemeToggle.js";
import { useDocumentTitle } from "./utils.js";

const FrontDesk = () => {
  useDocumentTitle('Front desk')
  const socket = useContext(SocketContext);
  const [raceSessions, setRaceSessions] = useState([]);
  const [newSessionName, setNewSessionName] = useState("");
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [sessionError, setSessionError] = useState("");
  const [confirmError, setConfirmError] = useState("");

  useEffect(() => {
    if (!socket) return;

    socket.on('state-update', (state) => {
      const updatedSessions = state.raceSessions
          .filter(s => s.status !== 'in-progress' && s.status !== 'Finished')
          .map((session) => ({
            ...session,
            drivers: session.drivers.concat(
              Array.from({ length: 8 - session.drivers.length }).map(() => ({
                id: Date.now() + Math.random(),
                name: "",
              }))
            ),
        }));
      setRaceSessions(updatedSessions);
    });

    socket.emit('request-full-state');

    return () => {
      socket.off("state-update");
    };
  }, [socket]);

  const addRaceSession = () => {
    if (!newSessionName.trim()) {
      setSessionError("Please enter a session name.");
      return;
    }
    setSessionError("");
    socket.emit('add-session', { sessionName: newSessionName }, (response) => {
      if (response.success) {
        setNewSessionName("");
      } else {
        setSessionError(response.error || 'Failed to add session.');
      }
    });
  };

  const deleteRaceSession = (sessionId) => {
    socket.emit('delete-session', { sessionId }, (response) => {
      if (!response.success) {
        alert(response.error || "Failed to delete session");
      }
    });
  };

  const confirmSession = (sessionId) => {
    const session = raceSessions.find((s) => s.id === sessionId);
    const drivers = session.drivers.map((d) => d.name.trim()).filter(Boolean);
    const unique = [...new Set(drivers.map(n => n.toLowerCase()))];
    if (drivers.length !== unique.length) {
      setConfirmError("Driver names must be unique within a session.");
      return;
    }
    setConfirmError("");
    socket.emit("confirm-session", { sessionId, drivers }, (response) => {
      if (response.success) {
        setEditingSessionId(null);
      } else {
        setConfirmError(response.error || "Failed to confirm session.");
      }
    });
  };

  const handleDriverEdit = (sessionId, driverIndex, newName) => {
    setRaceSessions(prev =>
      prev.map((session) =>
        session.id === sessionId
          ? {
              ...session,
              drivers: session.drivers.map((driver, i) =>
                i === driverIndex ? { ...driver, name: newName } : driver
              ),
            }
          : session
      )
    );
  };

  const startEditing = (sessionId) => {
    setConfirmError("");
    setEditingSessionId(sessionId);
  };

  const cancelEditing = () => {
    setConfirmError("");
    setEditingSessionId(null);
  };

  return (
    <div className="app-screen fd-page">

      <div className="corner-btn-wrapper">
        <ThemeToggle/>
      </div>
      
      <div className="grid-bg"/>

      <div className="fd-content">

        <div>
          <h1>Front Desk</h1>
          <p className="rc-label">Session &amp; driver management</p>
        </div>

        <section className="rc-card fd-add-panel">
          <h2>New Race Session</h2>
          <div className="fd-add-row">
            <div className="fd-add-field">
              <label htmlFor="fd-session-name" className="rc-form-label">Session name</label>
              <input
                className="rc-input"
                type="text"
                value={newSessionName}
                onChange={(e) => { setNewSessionName(e.target.value); setSessionError(""); }}
                onKeyDown={ (e) => {if (e.key === 'Enter') addRaceSession(); }}
                placeholder="e.g. Junior Sprint — Heat 1"
                autoComplete="off"
              />
            </div>
            <button
              className="rc-btn rc-btn--primary fd-add-btn"
              onClick={addRaceSession}
              disabled={!newSessionName.trim()}
            >
              Add session
            </button>
          </div>
          {sessionError && (
            <p className="c-notice rc-notice--red">{sessionError}</p>
          )}
        </section>

        <section>
          <div className="fd-sessions-header">
            <h2>Race Sessions</h2>
            <span className="rc-label">{raceSessions.length} queued</span>
          </div>

          {raceSessions.length === 0 ? (
            <div className="fd-empty">
              <p>No sessions queued. Add one above to get started.</p>
            </div>
          ) : (
            <div className="fd-session-list">
              {raceSessions.map((session) => {
                const isEditing = editingSessionId === session.id;
                const filledCount = session.drivers.filter(d => d.name.trim()).length;

                return (
                  <div
                    key={session.id}
                    className={`fd-session-card ${isEditing ? 'fd-session-card--editing' : ''}`}
                  >
                    <div className="fd-session-card__header">
                      <div>
                        <h3>{session.sessionName}</h3>
                        <span className="rc-label">
                          {filledCount} / 8 drivers assigned
                        </span>
                      </div>
                      <div className="fd-session-card__actions">
                        {isEditing ? (
                          <>
                            <button
                              className="rc-btn rc-btn--success rc-btn--sm"
                              onClick={() => confirmSession(session.id)}
                            >
                              Confirm
                            </button>
                            <button
                              className="rc-btn rc-btn--ghost rc-btn--sm"
                              onClick={cancelEditing}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            className="rc-btn rc-btn--ghost rc-btn--sm"
                            onClick={() => startEditing(session.id)}
                          >
                            Edit
                          </button>
                        )}
                        <button
                          className="rc-btn rc-btn--danger rc-btn--sm"
                          onClick={() => deleteRaceSession(session.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {isEditing && confirmError && (
                      <p className="rc-notice rc-notice--red">{confirmError}</p>
                    )}

                    <div className="fd-driver-grid">
                      {session.drivers.map((driver, index) => (
                        <div key={driver.id || index} className="fd-driver-row">
                          <span className="rc-car-num">{index + 1}</span>
                          <input
                            className={`rc-input fd-driver-input ${!isEditing ? 'fd-driver-input--readonly' : ''}`}
                            type="text"
                            value={driver.name || ""}
                            disabled={!isEditing}
                            onChange={(e) => handleDriverEdit(session.id, index, e.target.value)}
                            placeholder={`Driver ${index + 1}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default FrontDesk;